import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Portal } from './Portal';
import { useT } from '../i18n';

interface ReportMessage {
  id: string;
  contactName: string;
  contactPhone: string;
  status: string;
  sentAt: string | null;
  errorMessage: string | null;
  sessionName: string | null;
  receiptStatus: string | null;
}

interface Health {
  status: string;
  stoppedAt: string | null;
  stopTrigger: string | null;
  stopRule: 'OPT_OUT_RATE' | 'FAILURE_RATE' | 'DELIVERY_RATE' | 'MANUAL' | null;
  metrics: {
    sent: number; delivered: number; read: number; failed: number; replied: number; optedOut: number;
    deliveryRate: number; failureRate: number; replyRate: number; optOutRate: number; processed: number;
  };
  thresholds: { optOutRate: number; deliveryBelow: number; failureRate: number; minSample: number };
}

interface Report {
  campaign: {
    id: string; name: string; status: string; createdBy: string | null;
    createdAt: string; startedAt: string | null; completedAt: string | null;
    session: { name: string; displayName: string | null; status: string; provider: string } | null;
  };
  stats: { total: number; sent: number; failed: number; pending: number; skipped: number; delivered: number; read: number; receiptsKnown: number };
  rates: { progress: number; delivery: number; read: number; failure: number };
  failures: Array<{ reason: string; count: number }>;
  sessions: Array<{ name: string; total: number; sent: number; failed: number; pending: number }>;
  messages: { items: ReportMessage[]; page: number; pageSize: number; total: number; pages: number };
}

const STATUS_FILTERS = ['', 'SENT', 'FAILED', 'PENDING'] as const;

// O servidor agrupa os motivos em inglês; aqui só escolhemos o texto do idioma atual
const SYSTEM_ERROR_REASON = 'System error — the message was not attempted';

const FAILURE_KEYS: Record<string, string> = {
  [SYSTEM_ERROR_REASON]: 'fail.system',
  'Number is not on WhatsApp': 'fail.notOnWhatsApp',
  'Media file could not be downloaded': 'fail.media',
  'Provider rejected the credentials': 'fail.credentials',
  'Connection timeout': 'fail.timeout',
  'Contact unsubscribed': 'fail.unsubscribed',
  'Unknown error': 'fail.unknown',
};

// Espelha normalizeFailureReason do servidor: a linha guarda o erro cru do provedor
function normalizeFailure(raw: string): string {
  // Falha nossa: o número está certo, a tentativa é que não aconteceu
  if (/Invalid `prisma\.|connection pool|too many (clients|connections)|PrismaClient/i.test(raw)) return SYSTEM_ERROR_REASON;
  if (/exists"?\s*:\s*false|not on WhatsApp/i.test(raw)) return 'Number is not on WhatsApp';
  if (/AxiosError|getaddrinfo|ENOTFOUND|404|media/i.test(raw)) return 'Media file could not be downloaded';
  if (/401|Unauthorized/i.test(raw)) return 'Provider rejected the credentials';
  if (/timeout|ETIMEDOUT|ECONNRESET/i.test(raw)) return 'Connection timeout';
  if (/unsubscribed/i.test(raw)) return 'Contact unsubscribed';
  return raw;
}

const MESSAGE_STATUS = {
  SENT: { key: 'rep.msgSent', className: 'bg-emerald-100 text-emerald-800' },
  FAILED: { key: 'rep.msgFailed', className: 'bg-red-100 text-red-800' },
  PENDING: { key: 'rep.msgPending', className: 'bg-gray-100 text-gray-700' },
  PROCESSING: { key: 'rep.msgProcessing', className: 'bg-blue-100 text-blue-800' },
  SKIPPED: { key: 'rep.msgSkipped', className: 'bg-amber-100 text-amber-800' },
} as const;

const RECEIPTS: Record<string, { key: string; icon: string; className: string }> = {
  SENT: { key: 'rep.receiptSent', icon: '✓', className: 'text-gray-400' },
  DELIVERED: { key: 'rep.receiptDelivered', icon: '✓✓', className: 'text-gray-500' },
  READ: { key: 'rep.receiptRead', icon: '✓✓', className: 'text-blue-600' },
  PLAYED: { key: 'rep.receiptRead', icon: '✓✓', className: 'text-blue-600' },
};

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const formatNumber = (value: number) => value.toLocaleString('en-US');

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function Stat({ label, value, sub, tone = 'text-gray-900' }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-xl font-bold mt-0.5 ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export function CampaignReportModal({ campaignId, onClose, onOpenAnalytics }: {
  campaignId: string;
  onClose: () => void;
  onOpenAnalytics?: () => void;
}) {
  const t = useT();
  const [report, setReport] = useState<Report | null>(null);
  const [health, setHealth] = useState<Health | null>(null);
  const [stopping, setStopping] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '25' });
      if (status) params.set('status', status);
      if (debouncedSearch) params.set('search', debouncedSearch);
      const response = await fetch(`/api/campaigns/${campaignId}/report?${params}`, { headers: authHeaders() });
      if (!response.ok) {
        // O código do servidor é o que diz o que aconteceu; sem ele resta adivinhar
        const body = await response.json().catch(() => ({}));
        throw new Error([response.status, body.code, body.detail].filter(Boolean).join(' · '));
      }
      setReport(await response.json());
    } catch (error) {
      toast.error(`${t('rep.errLoad')} (${error instanceof Error ? error.message : '?'})`);
    } finally {
      setLoading(false);
    }
  }, [campaignId, page, status, debouncedSearch, t]);

  useEffect(() => {
    load();
  }, [load]);

  // A saúde é outra pergunta: como está indo agora, e por que parou se parou
  const loadHealth = useCallback(async () => {
    try {
      const response = await fetch(`/api/campaigns/${campaignId}/health`, { headers: authHeaders() });
      if (!response.ok) return;
      setHealth(await response.json());
    } catch {
      // Sem a saúde o relatório continua servindo
    }
  }, [campaignId]);

  useEffect(() => {
    loadHealth();
    // Enquanto a campanha está saindo, o número muda sozinho na tela
    const timer = setInterval(loadHealth, 30000);
    return () => clearInterval(timer);
  }, [loadHealth]);

  const stopCampaign = async () => {
    if (!window.confirm(t('hlt.stopConfirm'))) return;
    setStopping(true);
    try {
      const response = await fetch(`/api/campaigns/${campaignId}/stop`, { method: 'POST', headers: authHeaders() });
      if (!response.ok) throw new Error(String(response.status));
      toast.success(t('hlt.stopped'));
      await Promise.all([load(), loadHealth()]);
    } catch {
      toast.error(t('hlt.errStop'));
    } finally {
      setStopping(false);
    }
  };

  // Falhas que são nossas: o cliente devolve as mensagens para a fila sem perder os contatos
  const systemFailures = report?.failures.find(f => f.reason === SYSTEM_ERROR_REASON)?.count || 0;

  const retrySystemFailures = async () => {
    setRetrying(true);
    try {
      const response = await fetch(`/api/campaigns/${campaignId}/retry-system-failures`, {
        method: 'POST', headers: authHeaders(),
      });
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      toast.success(t('rep.sysFailOk', { count: formatNumber(data.requeued) }));
      await Promise.all([load(), loadHealth()]);
    } catch {
      toast.error(t('rep.sysFailErr'));
    } finally {
      setRetrying(false);
    }
  };

  // A frase da parada é montada aqui, no idioma da tela, a partir do código e dos números
  const stopSentence = () => {
    if (!health?.stopRule) return health?.stopTrigger || '';
    const m = health.metrics;
    const th = health.thresholds;
    switch (health.stopRule) {
      case 'OPT_OUT_RATE':
        return t('hlt.stopOPT_OUT_RATE', { optedOut: m.optedOut, sent: m.sent, rate: m.optOutRate, limit: th.optOutRate });
      case 'FAILURE_RATE':
        return t('hlt.stopFAILURE_RATE', { failed: m.failed, processed: m.processed, rate: m.failureRate, limit: th.failureRate });
      case 'DELIVERY_RATE':
        return t('hlt.stopDELIVERY_RATE', { rate: m.deliveryRate, limit: th.deliveryBelow });
      default:
        return t('hlt.stopMANUAL');
    }
  };

  useEffect(() => {
    setPage(1);
  }, [status, debouncedSearch]);

  const download = async () => {
    try {
      const response = await fetch(`/api/campaigns/${campaignId}/report/download`, { headers: authHeaders() });
      if (!response.ok) throw new Error(String(response.status));
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${report?.campaign.name || 'campaign'}-report.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(t('rep.okExport'));
    } catch {
      toast.error(t('rep.errExport'));
    }
  };

  const campaignStatusLabel = (value: string) => {
    const map: Record<string, string> = {
      RUNNING: 'rep.statusRunning', PAUSED: 'rep.statusPaused', COMPLETED: 'rep.statusCompleted',
      PENDING: 'rep.statusPending', SCHEDULED: 'rep.statusScheduled',
    };
    return map[value] ? t(map[value]) : value;
  };

  const failureLabel = (raw: string) => {
    const reason = normalizeFailure(raw);
    return FAILURE_KEYS[reason] ? t(FAILURE_KEYS[reason]) : reason;
  };

  const stats = report?.stats;
  const processed = stats ? stats.sent + stats.failed + stats.skipped : 0;

  return (
    <Portal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[95vh] overflow-y-auto">
          {/* Cabeçalho */}
          <div className="sticky top-0 z-10 bg-white border-b px-6 py-4 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span dir="auto">{report?.campaign.name || t('rep.title')}</span>
                {report && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    report.campaign.status === 'RUNNING' ? 'bg-emerald-100 text-emerald-800'
                      : report.campaign.status === 'PAUSED' ? 'bg-amber-100 text-amber-800'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {campaignStatusLabel(report.campaign.status)}
                  </span>
                )}
              </h3>
              {report && (
                <p className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3">
                  {report.campaign.createdBy && <span dir="auto">{t('rep.createdBy')}: {report.campaign.createdBy}</span>}
                  <span>{t('rep.createdAt')}: <bdi dir="ltr">{formatDateTime(report.campaign.createdAt)}</bdi></span>
                  {report.campaign.startedAt && <span>{t('rep.startedAt')}: <bdi dir="ltr">{formatDateTime(report.campaign.startedAt)}</bdi></span>}
                  {report.campaign.completedAt && <span>{t('rep.completedAt')}: <bdi dir="ltr">{formatDateTime(report.campaign.completedAt)}</bdi></span>}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              {onOpenAnalytics && (
                <button onClick={onOpenAnalytics} className="text-sm px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700">
                  {t('rep.analytics')}
                </button>
              )}
              <button onClick={download} className="text-sm px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50">
                {t('rep.export')}
              </button>
              <button onClick={load} disabled={loading} className="text-sm px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
                ↻ {t('rep.refresh')}
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2" title={t('conn.close')}>✕</button>
            </div>
          </div>

          {loading && !report && <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>}

          {report && stats && (
            <div className={`p-6 space-y-5 ${loading ? 'opacity-60' : ''}`}>
              {/* A campanha parou: o motivo primeiro, antes de qualquer número */}
              {health?.stoppedAt && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                  <p className="font-semibold text-red-900">🛑 {t('hlt.stoppedTitle')}</p>
                  <p className="text-sm text-red-800 mt-1">{stopSentence()}</p>
                </div>
              )}

              {/* Saúde enquanto ela está saindo */}
              {health && report.campaign.status === 'RUNNING' && (
                <div className="rounded-xl border border-gray-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <h4 className="font-semibold text-gray-900">{t('hlt.title')}</h4>
                    <button
                      onClick={stopCampaign}
                      disabled={stopping}
                      className="text-xs px-3 py-1.5 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      {t('hlt.stopButton')}
                    </button>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 text-center">
                    {([
                      ['hlt.sent', health.metrics.sent, 'text-gray-900'],
                      ['hlt.delivered', health.metrics.delivered, 'text-emerald-700'],
                      ['hlt.read', health.metrics.read, 'text-sky-700'],
                      ['hlt.failed', health.metrics.failed, health.metrics.failed ? 'text-red-600' : 'text-gray-900'],
                      ['hlt.replied', health.metrics.replied, 'text-indigo-700'],
                      ['hlt.optedOut', health.metrics.optedOut, health.metrics.optedOut ? 'text-amber-700' : 'text-gray-900'],
                    ] as const).map(([key, value, tone]) => (
                      <div key={key} className="rounded-lg bg-gray-50 p-2">
                        <p className={`text-lg font-bold ${tone}`}>{formatNumber(value)}</p>
                        <p className="text-[11px] text-gray-500">{t(key)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Progresso */}
              <div className="rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-medium text-gray-700">{t('rep.progress')}</span>
                  <span className="text-gray-600">
                    {t('rep.progressOf', { done: formatNumber(processed), total: formatNumber(stats.total) })} · {report.rates.progress}%
                  </span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex" dir="ltr">
                  <div className="bg-emerald-500 h-full" style={{ width: `${(stats.sent / Math.max(stats.total, 1)) * 100}%` }} />
                  <div className="bg-red-400 h-full" style={{ width: `${(stats.failed / Math.max(stats.total, 1)) * 100}%` }} />
                  <div className="bg-amber-300 h-full" style={{ width: `${(stats.skipped / Math.max(stats.total, 1)) * 100}%` }} />
                </div>
              </div>

              {/* Números */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <Stat label={t('rep.kpiTotal')} value={formatNumber(stats.total)} />
                <Stat label={t('rep.kpiSent')} value={formatNumber(stats.sent)} tone="text-emerald-700" />
                <Stat label={t('rep.kpiDelivered')} value={`${report.rates.delivery}%`} sub={`${formatNumber(stats.delivered)} ${t('rep.ofSent')}`} tone="text-blue-700" />
                <Stat label={t('rep.kpiRead')} value={`${report.rates.read}%`} sub={`${formatNumber(stats.read)} ${t('rep.ofDelivered')}`} tone="text-indigo-700" />
                <Stat label={t('rep.kpiFailed')} value={formatNumber(stats.failed)} sub={`${report.rates.failure}%`} tone={stats.failed ? 'text-red-600' : 'text-gray-900'} />
                <Stat label={t('rep.kpiPending')} value={formatNumber(stats.pending)} />
              </div>

              {stats.sent > stats.receiptsKnown && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  {t('rep.receiptsPending', { count: formatNumber(stats.sent - stats.receiptsKnown) })}
                </p>
              )}

              {systemFailures > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-900">
                      {t('rep.sysFailTitle', { count: formatNumber(systemFailures) })}
                    </p>
                    <p className="text-xs text-amber-800 mt-0.5">{t('rep.sysFailBody')}</p>
                  </div>
                  <button
                    onClick={retrySystemFailures}
                    disabled={retrying}
                    className="shrink-0 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-60"
                  >
                    {t('rep.sysFailAction')}
                  </button>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Motivos de falha */}
                <div className="rounded-xl border border-gray-200 p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">{t('rep.failures')}</h4>
                  {report.failures.length === 0 ? (
                    <p className="text-sm text-gray-400">{t('rep.noFailures')}</p>
                  ) : (
                    <div className="space-y-2">
                      {report.failures.map(failure => (
                        <div key={failure.reason}>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-700">
                              {failureLabel(failure.reason)}
                            </span>
                            <span className="font-semibold text-gray-900">{formatNumber(failure.count)}</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full mt-1 overflow-hidden">
                            <div className="h-full bg-orange-400 rounded-full"
                                 style={{ width: `${(failure.count / Math.max(stats.failed, 1)) * 100}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Conexões */}
                <div className="rounded-xl border border-gray-200 p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">{t('rep.bySession')}</h4>
                  <div className="space-y-2">
                    {report.sessions.map(session => (
                      <div key={session.name || 'none'} className="flex items-center justify-between text-sm rounded-lg bg-gray-50 px-3 py-2">
                        <span className="text-gray-700 truncate" dir="auto">{session.name || t('rep.notAssigned')}</span>
                        <span className="text-xs text-gray-600 whitespace-nowrap flex gap-2">
                          {session.sent > 0 && (
                            <span className="text-emerald-700">
                              <b>{formatNumber(session.sent)}</b> {t('rep.msgSent')}
                            </span>
                          )}
                          {session.failed > 0 && (
                            <span className="text-red-600">
                              <b>{formatNumber(session.failed)}</b> {t('rep.msgFailed')}
                            </span>
                          )}
                          {session.pending > 0 && (
                            <span className="text-gray-500">
                              <b>{formatNumber(session.pending)}</b> {t('rep.msgPending')}
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Mensagens */}
              <div className="rounded-xl border border-gray-200">
                <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b">
                  <h4 className="font-semibold text-gray-900">
                    {t('rep.messages')} <span className="text-gray-400 font-normal">({formatNumber(report.messages.total)})</span>
                  </h4>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                      {STATUS_FILTERS.map(option => (
                        <button
                          key={option || 'all'}
                          onClick={() => setStatus(option)}
                          className={`px-2.5 py-1 text-xs rounded-md ${status === option ? 'bg-white shadow-sm font-semibold' : 'text-gray-600'}`}
                        >
                          {option === '' ? t('rep.filterAll') : t(MESSAGE_STATUS[option as keyof typeof MESSAGE_STATUS].key)}
                        </button>
                      ))}
                    </div>
                    <input
                      value={search}
                      onChange={event => setSearch(event.target.value)}
                      placeholder={t('rep.search')}
                      className="text-sm border border-gray-300 rounded-md px-3 py-1.5 w-52"
                    />
                  </div>
                </div>

                {report.messages.items.length === 0 ? (
                  <p className="p-10 text-center text-sm text-gray-400">{t('rep.empty')}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-start text-gray-500">
                        <tr>
                          <th className="text-start px-4 py-2">{t('rep.colContact')}</th>
                          <th className="text-start px-4 py-2">{t('rep.colStatus')}</th>
                          <th className="text-start px-4 py-2">{t('rep.colDelivery')}</th>
                          <th className="text-start px-4 py-2">{t('rep.colSentAt')}</th>
                          <th className="text-start px-4 py-2">{t('rep.colDetails')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.messages.items.map(message => {
                          const statusInfo = MESSAGE_STATUS[message.status as keyof typeof MESSAGE_STATUS];
                          const receipt = message.receiptStatus ? RECEIPTS[message.receiptStatus] : null;
                          // O nome costuma ser o próprio número (importação por planilha): não repetir
                          const name = message.contactName?.replace(/\D/g, '') === message.contactPhone.replace(/\D/g, '')
                            ? null : message.contactName;
                          return (
                            <tr key={message.id} className="border-t hover:bg-gray-50">
                              <td className="px-4 py-2">
                                {name && <div className="font-medium text-gray-900" dir="auto">{name}</div>}
                                <div className={name ? 'text-xs text-gray-500' : 'font-medium text-gray-900'} dir="ltr">{message.contactPhone}</div>
                              </td>
                              <td className="px-4 py-2">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusInfo?.className || 'bg-gray-100 text-gray-700'}`}>
                                  {statusInfo ? t(statusInfo.key) : message.status}
                                </span>
                              </td>
                              <td className="px-4 py-2">
                                {receipt ? (
                                  <span className={receipt.className} title={t(receipt.key)}>{receipt.icon}</span>
                                ) : (
                                  <span className="text-gray-300" title={t('rep.receiptNone')}>—</span>
                                )}
                              </td>
                              <td className="px-4 py-2 text-gray-600 whitespace-nowrap"><bdi dir="ltr">{formatDateTime(message.sentAt)}</bdi></td>
                              <td className="px-4 py-2 text-gray-500 max-w-xs">
                                <span className="block truncate" title={message.errorMessage || ''}>
                                  {message.errorMessage ? failureLabel(message.errorMessage) : message.sessionName || '—'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {report.messages.pages > 1 && (
                  <div className="flex items-center justify-between p-3 border-t text-sm">
                    <span className="text-gray-500">{t('rep.pageOf', { page: report.messages.page, pages: report.messages.pages })}</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPage(current => Math.max(current - 1, 1))}
                        disabled={report.messages.page <= 1}
                        className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
                      >
                        {t('camp.previous')}
                      </button>
                      <button
                        onClick={() => setPage(current => current + 1)}
                        disabled={report.messages.page >= report.messages.pages}
                        className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
                      >
                        {t('camp.next')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}
